import { RULES } from './rules';
import type { MortgageResult } from './types';

/**
 * Core Swiss affordability computation. Pure: same inputs -> same output.
 *
 * @param price     Purchase price / property value (CHF).
 * @param income    Gross household annual income (CHF).
 * @param equityCHF Equity applied, as an absolute CHF amount (ImmoScout-style).
 *                  For a preset percentage p, pass `equityFromPercent(price, p)`.
 */
export function computeMortgage(price: number, income: number, equityCHF: number): MortgageResult {
  const equityPaid = Math.max(0, equityCHF);
  const mortgage = Math.max(0, price - equityPaid);
  const ltv = price > 0 ? mortgage / price : 0;
  const second = Math.max(0, mortgage - RULES.firstMortgageLtv * price);
  const interest = RULES.calcRate * mortgage;
  const amort = second / RULES.amortYears;
  const maint = RULES.maintenance * price;
  const annualCost = interest + amort + maint;
  const monthly = annualCost / 12;
  const ratio = income > 0 ? annualCost / income : Infinity;

  // Max. affordable price at this LTV. annualCost(price) = price * factor, so the
  // largest price with annualCost <= income/3 is (income/3) / factor.
  const factor =
    RULES.calcRate * ltv +
    Math.max(0, ltv - RULES.firstMortgageLtv) / RULES.amortYears +
    RULES.maintenance;
  const maxPrice = factor > 0 ? (income * RULES.affordabilityMax) / factor : 0;

  return {
    equityPaid,
    mortgage,
    ltv,
    second,
    interest,
    amort,
    maint,
    annualCost,
    monthly,
    ratio,
    maxPrice,
  };
}

/** Convert a preset equity percentage (0..1) to an absolute CHF amount for `price`. */
export function equityFromPercent(price: number, percent: number): number {
  return Math.max(0, price) * percent;
}
